import {defineStore} from "pinia";
import {Account, AccountType, VestingPeriods} from "@/models/store/account";
import apiFactory from "@/api/factory.api";
import factoryApi from "@/api/factory.api";
import {ConnectionError, ConnectionInfo, ConnectionType} from "@/api/wallet.connecton.api";
import {useToast} from "vue-toastification";
import {RequestResponse} from '@/models/request-response';
import {useConfigurationStore} from "./configuration.store";
import {Delegations, UnbondingDelegations} from "@/models/store/staking";
import {Rewards} from "@/models/store/distribution";
import {TxBroadcastError, TxData} from "@/api/tx.broadcast.base.api";
import {StoreLogger} from "@/services/logged.service";
import {ServiceTypeEnum} from "@/services/logger/service-type.enum";
import {LogLevel} from '@/services/logger/log-level';
import {BigDecimal} from "@/models/store/big.decimal";
import {useBlockStore} from "./block.store";
import i18n from "@/plugins/i18n";
import {useProposalsStore} from "./proposals.store";
import {VoteOption} from "@/models/store/proposal";
import TxToast from "@/components/commons/TxToast.vue";
import {Coin} from "@/models/store/common";
import {calculateLockedVesting} from "@/utils/vesting-utils";
import {FaucetErrorEnum} from "@/models/faucet";
import {useLoyaltyDropStore} from "@/store/boost.store";
import {TxRaw} from "@/api/cosmostx/tx";

const toast = useToast();
const logger = new StoreLogger(ServiceTypeEnum.USER_STORE);

export interface MetamaskConnectionInfo {
  address: string;
  networkId: number;
}
export interface UserState {
  connectionInfo: ConnectionInfo
  account: Account
  balance: bigint
  vestingAccLocked: bigint,
  spendableBalance: Coin[],
  rewards: Rewards
  delegations: Delegations
  undelegations: UnbondingDelegations,
  metamaskConnectionInfo: MetamaskConnectionInfo
}

const connectionInfoName = 'connectionInfo';
const metamaskConnectionInfoName = 'metamaskConnectionInfo';


export const useUserStore = defineStore({
  id: 'userStore',

  state: (): UserState => {
    return {
      [connectionInfoName]: new ConnectionInfo(),
      account: Account.disconnected,
      balance: 0n,
      vestingAccLocked: 0n,
      spendableBalance: [],
      rewards: new Rewards(),
      delegations: new Delegations(),
      undelegations: new UnbondingDelegations(),
      [metamaskConnectionInfoName]: {address: '', networkId: -1}
    };
  },
  actions: {
    async reconnect(onSuccess?: (connectionInfo: ConnectionInfo) => void){
      logger.logToConsole(LogLevel.DEBUG, 'reconnect: ', JSON.stringify(this.connectionInfo));
      if(this.connectionInfo.connectionType === ConnectionType.Keplr){
        await this.connectKeplr(onSuccess);
      } else if(this.connectionInfo.connectionType === ConnectionType.Address){
        await this.connectAsAddress(this.connectionInfo.account, onSuccess);
      } else if(this.connectionInfo.connectionType === ConnectionType.Cosmostation){
        await this.connectCosmostation(onSuccess);
      } else if(this.connectionInfo.connectionType === ConnectionType.Leap){
        await this.connectLeap(onSuccess);
      }
    },
    async reconnectMetamask(){
      logger.logToConsole(LogLevel.DEBUG, 'reconnect: ', JSON.stringify(this.metamaskConnectionInfo));
      if(this.metamaskConnectionInfo.address != '') {
        this.connectMetamask();
      }
    },
    async connectKeplr(onSuccess?: (connectionInfo: ConnectionInfo) => void) {
      await this.connect(
        apiFactory.walletApi().connectKeplr(),
        onSuccess
        );
    },
    async connectCosmostation(onSuccess?: (connectionInfo: ConnectionInfo) => void) {
      await this.connect(
        apiFactory.walletApi().connectCosmostation(),
        onSuccess
      );
    },
    async connectLeap(onSuccess?: (connectionInfo: ConnectionInfo) => void) {
      await this.connect(
        apiFactory.walletApi().connectLeap(),
        onSuccess
      );
    },
    async connectMetamask(onSuccess?: () => void) {
        return apiFactory.walletApi().connectMetamask().then(response => {
          if(response.isSuccess() && response.data != undefined){
            this.metamaskConnectionInfo = response.data;
            if(onSuccess) {
              onSuccess();
            }

            return response.data.address;
          }
        });
    },
    async connectAsAddress(address: string, onSuccess?: (connectionInfo: ConnectionInfo) => void) {
      await this.connect(
        apiFactory.walletApi().connectAddress(address),
        onSuccess
      );
    },
    async connect(
      connectionResponse: Promise<RequestResponse<ConnectionInfo, ConnectionError>>,
      onSuccess?: (connectionInfo: ConnectionInfo) => void
    ) {
      await connectionResponse.then(async (response) => {
        if (response.isError() || response.data === undefined) {
          logger.logToConsole(LogLevel.ERROR, 'Connection failed');
          toast.error('Connection failed');
          clearStateOnLogout(this);
        } else {
          this.connectionInfo = response.data;
          logger.logToConsole(LogLevel.DEBUG, 'Connected: ', JSON.stringify(this.connectionInfo));

          const address = this.connectionInfo.account;
          await this.fetchAccountData();
          if (this.isLoggedIn) {
            if (onSuccess !== undefined) {
              onSuccess(response.data);
            }
            logger.logToConsole(LogLevel.DEBUG, 'Address: "' + address + '" Connected');
            toast.success(i18n.global.t('TOAST.SUCCESS.ADDRESS_CONNECTED', {address: address}));
          } else {
            logger.logToConsole(LogLevel.ERROR, 'Address: "' + address + '" Connection failed');
            toast.error('Address: "' + address + '" Connection failed');
          }
        }
      });
    },
    async fetchAccountData(lockscreen = true) {
      const connectionInfo = this.connectionInfo;
      if (!checkIfConnected(connectionInfo)) {
        return false;
      }

      await apiFactory.accountApi().fetchAccount(connectionInfo.account, lockscreen).then(async response => {
        if (response.isSuccess() && response.data !== undefined) {
          const account = response.data;
          if (account.type !== AccountType.Nonexistent) {
            const allResults = await Promise.all([
              fetchBalance(connectionInfo, this, lockscreen),
              fetchSpendableBalances(connectionInfo, this, lockscreen),
              fetchRewards(connectionInfo, this, lockscreen),
              fetchDelegations(connectionInfo, this, lockscreen),
              fetchUnbondingDelegations(connectionInfo, this, lockscreen),
            ]);
            if (!allResults.every(r => r)) {
              clearStateOnLogout(this);
              return;
            }
          } else {
            clearStateForNonexistentAccount(this);
          }
          this.account = account;
        } else {
          clearStateOnLogout(this);
        }
      });
    },

    async calculateVestingLocked(latestBlTime: Date) {
      if (this.isContinuousVestingAccount) {
        if (this.account.continuousVestingData !== undefined) {
          this.vestingAccLocked = this.account.continuousVestingData.calculateVestingLocked(latestBlTime);
        }
      }
      else if (this.isPeriodicVestingAccount) {
        if (this.account.vestingPeriods !== undefined) {
          this.vestingAccLocked = calculatePeriodicVestingLocked(this.account.vestingPeriods, latestBlTime);
        }
      }
      else {
        this.vestingAccLocked = 0n;
      }
    },

    async delegate(validator: string, amount: number, fee?: number | undefined) {
      const connectionInfo = this.connectionInfo;
      await apiFactory.accountApi().delegate(connectionInfo, validator, amount, fee).then(async (resp) => {
        if (resp.isError()) {
          await onTxDeliveryFailure(connectionInfo, this, resp, 'Delegation of ' + amount + useConfigurationStore().config.stakingDenom  + ' to ' + validator + ' failed');
        } else {
          const allResults = await Promise.all([
            fetchBalance(connectionInfo, this, true),
            fetchRewards(connectionInfo, this, true),
            fetchDelegations(connectionInfo, this, true),
          ]);
          onTxDeliverySuccess(resp.data);
          onRefreshingError(allResults);

        }
      });
    },
    async sendTokens(target: string, amount: number, fee?: number | undefined, onSuccess?: () => void) {
      const connectionInfo = this.connectionInfo;
      await apiFactory.accountApi().sendTokens(connectionInfo, target, amount, fee).then(async (resp) => {
        if (resp.isError()) {
          await onTxDeliveryFailure(connectionInfo, this, resp, 'Transfer of ' + amount + useConfigurationStore().config.stakingDenom  + ' to ' + target + ' failed');
        } else {
          const allResults = await Promise.all([
            fetchBalance(connectionInfo, this, true),
            fetchSpendableBalances(connectionInfo, this, true),
            fetchRewards(connectionInfo, this, true),
            fetchDelegations(connectionInfo, this, true),
          ]);
          onTxDeliverySuccess(resp.data);
          onRefreshingError(allResults);
          onSuccess?.();
        }
      });
    },
    async simulateDelegation(validator: string, amount: number){
      const connectionInfo = this.connectionInfo;
      const response = await apiFactory.accountApi().simulate(connectionInfo, validator, amount);
      console.log(response);
      return response;
    },
    async simulateSending(target: string, amount: number){
      const connectionInfo = this.connectionInfo;
      const response = await apiFactory.accountApi().simulateSending(connectionInfo, target, amount);
      console.log(response);
      return response;
    },
    async redelegate(validatorSrc: string, validatorDst: string, amount: number) {
      const connectionInfo = this.connectionInfo;
      await apiFactory.accountApi().redelegate(connectionInfo, validatorSrc, validatorDst, amount).then(async (resp) => {
        if (resp.isError()) {
          await onTxDeliveryFailure(connectionInfo, this, resp, 'Redelegation of ' + amount + useConfigurationStore().config.stakingDenom  + ' to ' + validatorDst + ' failed');
        } else {
          const allResults = await Promise.all([
            fetchBalance(connectionInfo, this, true),
            fetchRewards(connectionInfo, this, true),
            fetchDelegations(connectionInfo, this, true),
          ]);
          onTxDeliverySuccess(resp.data);
          onRefreshingError(allResults);
        }
      });
    },
    async undelegate(validator: string, amount: number) {
      const connectionInfo = this.connectionInfo;
      await apiFactory.accountApi().undelegate(connectionInfo, validator, amount).then(async (resp) => {
        if (resp.isError()) {
          await onTxDeliveryFailure(connectionInfo, this, resp, 'Undelegation of ' + amount + useConfigurationStore().config.stakingDenom  + ' from ' + validator + ' failed');
        } else {
          const allResults = await Promise.all([
            fetchBalance(connectionInfo, this, true),
            fetchRewards(connectionInfo, this, true),
            fetchDelegations(connectionInfo, this, true),
            fetchUnbondingDelegations(connectionInfo, this, true),
          ]);
          onTxDeliverySuccess(resp.data);
          onRefreshingError(allResults);
        }
      });
    },
    async claimRewards() {
      const connectionInfo = this.connectionInfo;
      const validators = this.rewards.getAllValidatorsAddresses();
      await apiFactory.accountApi().claimRewards(connectionInfo, validators).then(async (resp) => {
        if (resp.isError()) {
          await onTxDeliveryFailure(connectionInfo, this, resp, 'Claiming rewards failed: ' + resp.error?.message);
        } else {
          const allResults = await Promise.all([
            fetchBalance(connectionInfo, this, true),
            fetchRewards(connectionInfo, this, true),
            fetchSpendableBalances(connectionInfo, this, true)
          ]);
          onTxDeliverySuccess(resp.data);
          onRefreshingError(allResults);
        }
      });
    },
    async claimInitialAirdrop(campaignId: string, extraAddress: string): Promise<boolean> {
      const connectionInfo = this.connectionInfo;
      return await apiFactory.accountApi().claimInitialAirDrop(connectionInfo, campaignId, (!extraAddress || extraAddress === '') ? this.account.address : extraAddress)
        .then(async (resp) => {
          if (resp.isError()) {
            await onTxDeliveryFailure(connectionInfo, this, resp, 'Claiming airdrop rewards failed: ' + resp.error?.message);
            return false;
          } else {
            const allResults = await Promise.all([
              fetchBalance(connectionInfo, useUserStore(), true),
            ]);
            onTxDeliverySuccess(resp.data);
            onRefreshingError(allResults);
            onClaimAirdropSuccess();
            return true;
          }
        });
    },
    async claimOtherAirdrop(campaignId: string, missionId: string): Promise<boolean> {
      const connectionInfo = useUserStore().connectionInfo;
      return await apiFactory.accountApi().claimAirDropMissions(connectionInfo, campaignId, missionId)
        .then(async (resp) => {
          if (resp.isError()) {
            await onTxDeliveryFailure(connectionInfo, this, resp, 'Claiming airdrop rewards failed: ' + resp.error?.message);
            return false;
          } else {
            const allResults = await Promise.all([
              fetchBalance(connectionInfo, useUserStore(), true),
            ]);
            onTxDeliverySuccess(resp.data);
            onRefreshingError(allResults);
            onClaimAirdropSuccess();
            return true;
          }
        });
    },

    async vote(option: VoteOption, proposalId: number){
      const connectionInfo = this.connectionInfo;
      apiFactory.accountApi().vote(this.connectionInfo, option, proposalId).then(async (resp) => {
        if (resp.isError()) {
          await onTxDeliveryFailure(connectionInfo, this, resp, 'Vote: ' + option + ' for proposal ' + proposalId + ' failed');
        } else {
            await useProposalsStore().fetchProposalById(
              proposalId,
              undefined,
              () => {
                onRefreshingError([true]);
              },
              true,
              true
            );
            onTxDeliverySuccess(resp.data);
        }
      });
    },
    async logOut() {
      logger.logToConsole(LogLevel.DEBUG, 'logOut before: ', JSON.stringify(this.connectionInfo));
      const address = this.connectionInfo.account;
      const prevConType = this.connectionInfo.connectionType;
      clearStateOnLogout(this);
      if (prevConType !== ConnectionType.Disconnected) {
        toast.success(i18n.global.t('TOAST.SUCCESS.ADDRESS_DISCONNECTED', {address: address}));
      }
      logger.logToConsole(LogLevel.DEBUG, 'logOut after: ', JSON.stringify(this.connectionInfo));
    },
    async topUpAccount(address: string, recaptchaToken: string, successCallback: () => void , failCallback: (errorType: FaucetErrorEnum) => void){
        await factoryApi.faucetApi().topUpAccount(address, recaptchaToken).then(res => {
          if(res.isSuccess()) {
            if(this.account.address) {
              fetchBalance(this.connectionInfo, this, true);
              fetchSpendableBalances(this.connectionInfo, this, true);
            }
            successCallback();
          } else {
            let errorType: FaucetErrorEnum = FaucetErrorEnum.UNKNOWN;
            if(res.error?.status == 429) {
              errorType = FaucetErrorEnum.TOO_MANY_REQUESTS;
            }
            failCallback(errorType);
          }
        });
    },
    async updateSpendables(lockscreen: boolean) {
      await fetchSpendableBalances(this.account.address, this, lockscreen);
    },

    async createVestingPoolLoyaltyDrop(vestingPoolName: string, amount:number, vestingPeriod: number, vestingType: string): Promise<boolean> {
      const connectionInfo = this.connectionInfo;
      return await apiFactory.accountApi().signMessageForCreateVestingPoolLoyaltyDrop(connectionInfo, vestingPoolName, amount,vestingPeriod, vestingType)
        .then(async (resp) => {
          if (resp.isError()) {
            await onTxDeliveryFailure(connectionInfo, this, resp, 'Creating loyaltyDrop vesting poll failed: ' + resp.error?.message);
            return false;
          } else {
            console.log("createVestingPoolLoyaltyDrop data:",resp.data);
            const txBytes = TxRaw.encode(resp.data as TxRaw).finish();
            const broadcastTransactionReq = {
              tx_bytes: Array.from(txBytes), // Converting Uint8Array to array for JSON serialization
            };
            const response = await useLoyaltyDropStore().broadcastSignedMessage( JSON.stringify(broadcastTransactionReq));
            if(response.isSuccess() && response.data) {
              console.log("!!!!!" + JSON.stringify(response));
              const allResults = await Promise.all([
                fetchBalance(connectionInfo, useUserStore(), true),
              ]);
              //
              // const dummyTxData: TxData = new TxData({
              //   code:0,
              //   transactionHash:response.data.txHash, events: [], gasUsed: 0, gasWanted: 0, height: 0, msgResponses: [], txIndex: 0
              // });
              // onTxBroadcastToLoyaltyDropBackendSuccess(dummyTxData);

              onTxBroadcastToLoyaltyDropBackendSuccess({
                  code:0,
                  transactionHash:response.data.txHash, gasUsed: 0, gasWanted: 0, height: 0
                });
              onRefreshingError(allResults);
              return true;
            } else {
              onClaimAirdropSuccess();//TODO:
              await onTxDeliveryFailure(connectionInfo, this, resp, 'Broadcast signed message error: ' + resp.error?.message);
              return false;
            }

          }
        });
    },
    async vestingPoolWithdrawAllAvailable(): Promise<boolean> {
      const connectionInfo = this.connectionInfo;
      return await apiFactory.accountApi().vestingPoolWithdrawAllAvailable(connectionInfo)
        .then(async (resp) => {
          if (resp.isError()) {
            await onTxDeliveryFailure(connectionInfo, this, resp, 'Withdrawing all available from vesting pools ' + resp.error?.message);
            return false;
          } else {
            const allResults = await Promise.all([
              fetchBalance(connectionInfo, useUserStore(), true),
            ]);
            onTxDeliverySuccess(resp.data);
            onRefreshingError(allResults);
            // onClaimAirdropSuccess();//TODO:
            return true;
          }
        });
    },
  },
  getters: {
    getConnectionType(): ConnectionType {
      return this.connectionInfo.connectionType;
    },
    isLoggedIn (): boolean {
       return this.account.type !== AccountType.Disconnected;
    },
    getAccount(): Account {
      return this.account;
    },
    isContinuousVestingAccount(): boolean {
      return this.account.type === AccountType.ContinuousVestingAccount
        && this.account.continuousVestingData !== undefined
        && useBlockStore().getLatestBlock.time.getTime() <= this.account.continuousVestingData?.endTime.getTime();
    },
    isPeriodicVestingAccount(): boolean {
      return this.account.type === AccountType.PeriodicContinuousVestingAccount
        && this.account.vestingPeriods !== undefined
        && useBlockStore().getLatestBlock.time.getTime() <= findMaxTime(this.account.vestingPeriods);
    },
    getAccountType(): AccountType {
      return this.account.type;
    },
    getBalance(): bigint {
      return this.balance;
    },
    getTotalRewards(): BigDecimal {
      return this.rewards.totalRewards;
    },
    getRewards():Rewards {
      return this.rewards;
    },
    getTotalUndelegating(): bigint {
      return this.undelegations.totalUndelegating;
    },
    getUndelegations(): UnbondingDelegations {
      return this.undelegations;
    },
    getDelegations(): Delegations {
      return this.delegations;
    },
    getTotalDelegated(): bigint {
      return this.delegations.totalDelegated;
    },
    getVestingLockAmount() : bigint {
      return this.vestingAccLocked;
    },
    getTotal() : bigint {
      return this.undelegations.totalUndelegating + this.delegations.totalDelegated + this.balance;
    },
    hasUndelegations(): boolean {
      return this.undelegations.hasUnbondingDelegations();
    },
    hasDelegations(): boolean {
      return this.delegations.hasDelegations();
    },
    getAccountVestingDetails(): VestingPeriods[] | undefined {
      return this.account.vestingPeriods;
    },
    getSpendableBalance(): bigint | undefined {
      if (this.spendableBalance.length) {
        return this.spendableBalance[0].amount;
      }
    },
    getMaxTime(): number {
      if (this.account.vestingPeriods) {
        return findMaxTime(this.account.vestingPeriods);
      }
      return 0;
    }
  },
  persist: {
    enabled: true,
    strategies: [
      { storage: sessionStorage, paths: [connectionInfoName, metamaskConnectionInfoName] },
    ]
  }
});

function checkIfConnected(connectionInfo: ConnectionInfo): boolean {
  if (connectionInfo.connectionType === ConnectionType.Disconnected) {
    toast.error('Not connected');
    return false;
  }
  return true;
}

function clearStateForNonexistentAccount(state: UserState) {
  state.balance = 0n;
  state.vestingAccLocked = 0n;
  state.rewards = new Rewards();
  state.delegations = new Delegations();
  state.undelegations = new UnbondingDelegations();
  state.spendableBalance = [];
}

function clearStateOnLogout(state: UserState) {

  state.connectionInfo = new ConnectionInfo();
  logger.logToConsole(LogLevel.DEBUG, 'clearStateOnLogout: ', JSON.stringify(state.connectionInfo));
  state.account = Account.disconnected;
  clearStateForNonexistentAccount(state);
}

export async function fetchBalance(connectionInfo: ConnectionInfo, state: UserState, lockscreen: boolean): Promise<boolean> {
  const address = connectionInfo.account;
  const denom = useConfigurationStore().config.stakingDenom;
  const response = await apiFactory.accountApi().fetchBalance(address, denom, lockscreen);
  if (response.isSuccess() && response.data !== undefined) {
    const balance = response.data;
    state.balance = balance.amount;
    return true;
  } else {
    return false;
  }
}

async function fetchSpendableBalances(connectionInfo: ConnectionInfo | string, state: UserState, lockscreen: boolean): Promise<boolean> {
  const address = connectionInfo instanceof ConnectionInfo ? connectionInfo.account : connectionInfo;
  const response = await apiFactory.accountApi().fetchSpendableBalances(address, lockscreen);
  if (response.isSuccess() && response.data !== undefined) {
    state.spendableBalance = response.data;
    return true;
  } else {
    return false;
  }
}

async function fetchDelegations(connectionInfo: ConnectionInfo, state: UserState, lockscreen: boolean): Promise<boolean> {
  const address = connectionInfo.account;
  const response = await apiFactory.accountApi().fetchDelegations(address, lockscreen);
  if (response.isSuccess() && response.data !== undefined) {
    state.delegations = response.data;
    return true;
  } else {
    return false;
  }
}

async function fetchUnbondingDelegations(connectionInfo: ConnectionInfo, state: UserState, lockscreen: boolean): Promise<boolean> {
  const address = connectionInfo.account;
  const response = await apiFactory.accountApi().fetchUnbondingDelegations(address, lockscreen);
  if (response.isSuccess() && response.data !== undefined) {
    state.undelegations = response.data;
    return true;
  } else {
    return false;
  }
}

async function fetchRewards(connectionInfo: ConnectionInfo, state: UserState, lockscreen: boolean): Promise<boolean> {
  const address = connectionInfo.account;
  const response = await apiFactory.accountApi().fetchRewards(address, lockscreen);
  if (response.isSuccess() && response.data !== undefined) {
    state.rewards = response.data;
    return true;
  } else {
    return false;
  }
}

async function onTxDeliveryFailure(connectionInfo: ConnectionInfo, state: UserState, response: RequestResponse<TxData | TxRaw, TxBroadcastError>, message: string) {
  logger.logToConsole(LogLevel.ERROR, message);
  toast.error(message);
  if (response.error?.hasTxData()) {
    await fetchBalance(connectionInfo, state, false);
  }
}

function onRefreshingError(allResults: boolean[]) {
  if (!allResults.every(r => r)) {
    logger.logToConsole(LogLevel.ERROR, 'Refereshing data error');
    toast.error('Refereshing data error');
    return;
  }
}

function onTxDeliverySuccess(tx?: TxData) {
  if (tx) {
    logger.logToConsole(LogLevel.DEBUG, `Tx: ${tx.transactionHash} success. GasUsed: ${tx.gasUsed}`);
    const content = {
      component: TxToast,
      props: {
        tx: tx
      },
    };
    toast.success(content);
  } else {
    logger.logToConsole(LogLevel.WARNING, `Tx delivered successfully but cannt get TX data`);
    toast.warning(`Tx delivered successfully but cannt get TX data`);
  }
}

function onTxBroadcastToLoyaltyDropBackendSuccess(tx?: TxData) {
  if (tx) {
    logger.logToConsole(LogLevel.DEBUG, `Tx: ${tx} success.}`);
    const content = {
      component: TxToast,
      props: {
        tx: tx
      },
    };
    toast.success(content);
  } else {
    logger.logToConsole(LogLevel.WARNING, `Tx delivered successfully but cannt get TX data`);
    toast.warning(`Tx delivered successfully but cannt get TX data`);
  }
}

function onClaimAirdropSuccess() {
   toast.success(i18n.global.t('AIRDROP.SUCCESS'));
}

// function onClaimAirdropSuccess() {
//   toast.success(i18n.global.t('AIRDROP.SUCCESS'));
// }


function findMaxTime(periods: VestingPeriods[]) {
 let maxTime = 0;
 periods.forEach(period => maxTime = period.endTime > maxTime ? period.endTime : maxTime);
 return maxTime*1000;
}

function calculatePeriodicVestingLocked  (periods: VestingPeriods[], latestBlockTime: Date) {
  const blockTime = latestBlockTime.getTime();
  let totalLocked = 0n;
  periods.forEach(period => {
    let sumAmount = 0n;
    period.amount.forEach((item) => sumAmount += item.amount);
    totalLocked += calculateLockedVesting(period.startTime*1000, period.endTime*1000, blockTime, sumAmount);
  });
  return totalLocked;
}
